import * as Sentry from '@sentry/react';

export type PhotoLogLevel = 'info' | 'warning' | 'error';

export type PhotoLogAttributes = Record<string, string | number | boolean>;

export interface ImageDataLike {
  data: ArrayLike<number>;
  width: number;
  height: number;
}

export interface ImageDataStats {
  width: number;
  height: number;
  redMin: number;
  redMax: number;
  redMean: number;
  zeroPixelRatio: number;
  firstPixel: string;
}

export function roundStat(value: number, digits = 2): number {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
}

export function flattenPhotoAttributes(data?: Record<string, unknown>): PhotoLogAttributes {
  const out: PhotoLogAttributes = {};
  if (!data) return out;

  for (const [key, value] of Object.entries(data)) {
    if (value === null || value === undefined) continue;
    if (typeof value === 'string' || typeof value === 'boolean') {
      out[key] = value;
    } else if (typeof value === 'number' && Number.isFinite(value)) {
      out[key] = value;
    } else {
      out[key] = String(value);
    }
  }
  return out;
}

export function photoLog(level: PhotoLogLevel, message: string, data?: Record<string, unknown>): void {
  const attributes = flattenPhotoAttributes(data);

  Sentry.addBreadcrumb({
    type: level === 'error' ? 'error' : 'info',
    category: 'photo.quality',
    message,
    level,
    data: attributes,
  });

  const logger = Sentry.logger;
  if (logger) {
    if (level === 'error') logger.error(message, attributes);
    else if (level === 'warning') logger.warn(message, attributes);
    else logger.info(message, attributes);
  }

  const line = `[PhotoSession] ${message}`;
  if (level === 'error') console.error(line, attributes);
  else if (level === 'warning') console.warn(line, attributes);
  else console.info(line, attributes);
}

/** Error-level event so Session Replay keeps the last minute. No photo bytes, no VIN. */
export function captureCaptureFailure(message: string, data?: Record<string, unknown>): void {
  photoLog('error', message, data);
  Sentry.captureMessage(message, {
    level: 'error',
    tags: { stage: 'photo-capture' },
    extra: data,
  });
}

/**
 * Red-channel stats plus how many pixels are pure black. A black canvas
 * shows zeroPixelRatio ≈ 1 and Laplacian sharpness 0.
 */
export function summarizeImageData(imageData: ImageDataLike): ImageDataStats {
  const { data, width, height } = imageData;
  const pixelCount = width * height;
  if (pixelCount <= 0 || data.length < 4) {
    return {
      width,
      height,
      redMin: 0,
      redMax: 0,
      redMean: 0,
      zeroPixelRatio: 1,
      firstPixel: '',
    };
  }

  let redMin = 255;
  let redMax = 0;
  let redSum = 0;
  let zeros = 0;

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    if (r < redMin) redMin = r;
    if (r > redMax) redMax = r;
    redSum += r;
    if (r === 0 && g === 0 && b === 0) zeros += 1;
  }

  return {
    width,
    height,
    redMin,
    redMax,
    redMean: roundStat(redSum / pixelCount),
    zeroPixelRatio: roundStat(zeros / pixelCount, 4),
    firstPixel: `${data[0]},${data[1]},${data[2]},${data[3]}`,
  };
}

export type QualityCanvasBackend = 'offscreen' | 'html';

export interface QualityImageData {
  imageData: ImageData;
  backend: QualityCanvasBackend;
}

const READ_CONTEXT: CanvasRenderingContext2DSettings = { willReadFrequently: true };

function drawQualitySample(
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  source: CanvasImageSource,
  size: number
): ImageData {
  ctx.drawImage(source, 0, 0, size, size);
  return ctx.getImageData(0, 0, size, size);
}

/**
 * Sample pixels for the sharpness check. OffscreenCanvas first — on Firefox
 * for Android a page canvas getImageData of an ImageBitmap comes back black
 * (the encode path already uses OffscreenCanvas for the same reason).
 * HTML canvas only when OffscreenCanvas or its 2d context is missing.
 */
export function readQualityImageData(source: CanvasImageSource, size: number): QualityImageData {
  if (typeof OffscreenCanvas !== 'undefined') {
    try {
      const canvas = new OffscreenCanvas(size, size);
      const ctx = canvas.getContext('2d', READ_CONTEXT);
      if (ctx) {
        return { imageData: drawQualitySample(ctx, source, size), backend: 'offscreen' };
      }
    } catch {
      // jsdom / incomplete OffscreenCanvas: HTML canvas below.
    }
  }

  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d', READ_CONTEXT);
  if (!ctx) {
    throw new Error('getContext(2d) returned null');
  }
  return { imageData: drawQualitySample(ctx, source, size), backend: 'html' };
}
